/**
 * LSP-15388 — module-scoped record of the `?uploadStarted` handoff already adopted.
 *
 * ActionUploadPhoto hands a just-started upload to the project page by navigating to
 * `/projects/:id?uploadStarted=<conversationId>`. EntityDetailPage consumes that on every
 * initializeComponent, which is more often than the handoff happens: a param-only project switch
 * typically keeps the query, and EntityDetailPage is re-created on each such switch. Per-instance
 * state cannot dedupe that, because the new project gets a fresh component — and it would then adopt
 * the previous project's upload as its own, putting one project's conversation into another
 * project's filter.
 *
 * So the marker lives at module scope, which outlives an in-SPA navigation. Mirrors
 * `incompleteUploadReminderState`, which EntityDetailPage already uses for the same reason.
 *
 * Deliberately a single value rather than a growing set: a new upload always means a new
 * conversation id, so only the most recent handoff can plausibly be replayed, and one slot keeps
 * this bounded for the life of the tab.
 */
let lastConsumedUploadHandoff: string | null = null;

/** True when this exact handoff has already been adopted and must not be adopted again. */
export function hasConsumedUploadHandoff(conversationId: string): boolean {
	return lastConsumedUploadHandoff === conversationId;
}

export function markUploadHandoffConsumed(conversationId: string): void {
	lastConsumedUploadHandoff = conversationId;
}

/**
 * Forget a handoff that turned out not to belong here (Cursor, Medium). The marker is claimed before
 * ownership can be known — that needs a getConversation — so a stale `?uploadStarted` naming another
 * project's conversation would otherwise burn the one slot and permanently stop the project that
 * DOES own the upload from adopting it. Only clears a matching id, so it cannot discard a later,
 * legitimate handoff.
 */
export function forgetUploadHandoffConsumed(conversationId: string): void {
	if (lastConsumedUploadHandoff === conversationId) {
		lastConsumedUploadHandoff = null;
	}
}

/** Test seam — production never needs to forget an unrelated consumed handoff. */
export function resetUploadHandoffStateForTests(): void {
	lastConsumedUploadHandoff = null;
}
